import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, TrendingUp, Users, Sparkles, XCircle, AlertTriangle, Loader2, CalendarClock, Percent, KeyRound, Banknote, UserPlus, Repeat, TrendingDown, Search, MailWarning } from "lucide-react";
import { adminApi } from "@/lib/admin-api";
import { useAdminAuth } from "@/lib/admin-auth";

const TABS = [["all", "All"], ["trial", "Trials"], ["active", "Paid"], ["past_due", "Past Due"], ["cancelled", "Cancelled"], ["expired", "Expired"]];
const FILTER_LABELS = { paid: "Paid", failed: "Failed Payments", renewals: "Renewals (7d)", discounts: "Active Discounts" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, logout } = useAdminAuth();
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("all");
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSec, setShowSec] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => { adminApi("/admin/overview").then(setOverview).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const qs = `status=${tab}${query ? `&q=${encodeURIComponent(query)}` : ""}`;
    adminApi(`/admin/garages?${qs}`).then((r) => setGarages(r.garages || [])).finally(() => setLoading(false));
  }, [tab, query]);

  const t = overview?.totals || {};
  const rev = overview?.revenue || {};
  const growth = overview?.growth || {};
  const renewals = overview?.upcoming_renewals || [];
  const trialsEnding = overview?.trials_ending_soon || [];
  const discounts = overview?.active_discounts || [];
  const scfg = overview?.stripe_config;

  const pickFilter = (f) => {
    setTab(f);
    document.getElementById("garage-table")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-midnight">
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10"><ShieldCheck className="h-4 w-4 text-brand" /></div>
          <div><p className="font-display text-[15px] font-bold tracking-widest text-white">OWNER PORTAL</p><p className="text-[11px] text-white/50">{admin?.email}</p></div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/owner/promotions")} data-testid="nav-promotions" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-white/80"><Percent className="h-4 w-4" />Promo Codes</button>
          <button onClick={() => navigate("/owner/promoters")} data-testid="nav-promoters" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-white/80"><Users className="h-4 w-4" />Promoters</button>
          <button onClick={() => setShowSec(true)} data-testid="admin-security-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-white/80"><KeyRound className="h-4 w-4" />Security {admin?.totp_enabled && <span className="rounded-full bg-success/20 px-1.5 text-[10px] text-success">2FA</span>}</button>
          <button onClick={async () => { await logout(); navigate("/owner/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-white/80"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="mb-6 font-display text-4xl font-bold uppercase text-white">SaaS Overview</h1>

        {overview && (
          <>
            <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Tile icon={TrendingUp} label="MRR" value={`£${(rev.mrr_gbp ?? 0).toLocaleString()}`} accent testid="tile-mrr" onClick={() => pickFilter("paid")} />
              <Tile icon={Users} label="Total Garages" value={t.garages ?? 0} testid="tile-garages" onClick={() => pickFilter("all")} />
              <Tile icon={Sparkles} label="Active Trials" value={t.active_trials ?? 0} testid="tile-trials" onClick={() => pickFilter("trial")} />
              <Tile icon={ShieldCheck} label="Paid" value={t.active_paid ?? 0} testid="tile-paid" onClick={() => pickFilter("paid")} />
              <Tile icon={XCircle} label="Cancelled" value={t.cancelled ?? 0} testid="tile-cancelled" onClick={() => pickFilter("cancelled")} />
              <Tile icon={AlertTriangle} label="Failed Payments" value={overview.failed_payments ?? 0} testid="tile-failed" onClick={() => pickFilter("failed")} />
              <Tile icon={CalendarClock} label="Renewals (7d)" value={renewals.length} testid="tile-renewals" onClick={() => pickFilter("renewals")} />
              <Tile icon={Percent} label="Active Discounts" value={discounts.length} testid="tile-discounts" onClick={() => pickFilter("discounts")} />
            </div>

            <p className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-white/40">Revenue & Growth</p>
            <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Tile icon={Banknote} label="ARR" value={`£${(rev.arr_gbp ?? 0).toLocaleString()}`} accent testid="tile-arr" onClick={() => pickFilter("paid")} />
              <Tile icon={UserPlus} label="New (30d)" value={growth.new_registrations_30d ?? 0} testid="tile-newregs" onClick={() => pickFilter("all")} />
              <Tile icon={CalendarClock} label="Trials Ending (7d)" value={trialsEnding.length} testid="tile-trials-ending" onClick={() => pickFilter("trials_ending")} />
              <Tile icon={Repeat} label="Trial Conversion" value={`${growth.trial_conversion_pct ?? 0}%`} testid="tile-conversion" sub="approx" />
              <Tile icon={TrendingDown} label="Churn (30d)" value={`${growth.churn_pct ?? 0}%`} testid="tile-churn" sub="approx" />
            </div>
            {rev.note && <p className="mb-6 rounded-lg bg-white/5 px-3 py-2 text-xs text-white/50">{rev.note}</p>}
            {scfg && !(scfg.secret_key_set && scfg.webhook_secret_set && scfg.price_ids_configured) && (
              <div data-testid="stripe-config-banner" className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-200">
                <p className="mb-1 font-bold">Stripe setup incomplete — running in demo mode.</p>
                <ul className="space-y-0.5 text-amber-200/80">
                  <li>• STRIPE_SECRET_KEY: {scfg.secret_key_set ? "✓ set" : "✗ missing"}</li>
                  <li>• STRIPE_WEBHOOK_SECRET: {scfg.webhook_secret_set ? "✓ set" : "✗ missing"}</li>
                  <li>• 6 Price IDs: {scfg.price_ids_configured ? "✓ all set" : `✗ missing ${Object.entries(scfg.price_ids).filter(([, v]) => !v).map(([k]) => k).join(", ")}`}</li>
                </ul>
                <p className="mt-1.5 text-amber-200/70">Add these in Emergent Secrets → Backend to enable live billing.</p>
              </div>
            )}

            {(renewals.length > 0 || discounts.length > 0 || trialsEnding.length > 0) && (
              <div className="mb-8 grid gap-4 md:grid-cols-3">
                <Panel title="Upcoming renewals (next 7 days)" empty={renewals.length === 0}>
                  {renewals.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-1.5 text-sm text-white/80">
                      <span className="truncate">{r.garage_name}</span>
                      <span className="text-white/50">{fmtDate(r.renews)} · £{(r.amount_gbp ?? 0).toLocaleString()}</span>
                    </div>
                  ))}
                </Panel>
                <Panel title="Trials ending (next 7 days)" empty={trialsEnding.length === 0}>
                  {trialsEnding.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-1.5 text-sm text-white/80" data-testid={`trial-ending-${r.id}`}>
                      <span className="truncate">{r.garage_name}</span>
                      <span className="text-white/50">{fmtDate(r.trial_end)}</span>
                    </div>
                  ))}
                </Panel>
                <Panel title="Active discounts" empty={discounts.length === 0}>
                  {discounts.map((d) => (
                    <div key={d.id} className="flex items-center justify-between py-1.5 text-sm text-white/80">
                      <span className="truncate">{d.garage_name}</span>
                      <span className="text-brand">{d.discount.type === "percent" ? `${d.discount.value}% off` : `£${d.discount.value} off`}</span>
                    </div>
                  ))}
                </Panel>
              </div>
            )}
          </>
        )}

        <div id="garage-table" className="mb-4 flex flex-wrap items-center gap-2">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide ${tab === k ? "bg-brand text-white" : "bg-white/5 text-white/60"}`}>{l}</button>
          ))}
          <button onClick={() => setTab("unverified")} data-testid="admin-tab-unverified" className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide ${tab === "unverified" ? "bg-brand text-white" : "bg-white/5 text-white/60"}`}>Unverified</button>
          {!TABS.some(([k]) => k === tab) && tab !== "unverified" && (
            <span data-testid="active-filter-chip" className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
              {FILTER_LABELS[tab] || tab}
              <button onClick={() => setTab("all")} className="text-white/70 hover:text-white">✕</button>
            </span>
          )}
          <form onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }} className="ml-auto flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
            <Search className="h-4 w-4 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-search" placeholder="Search name or email…" className="w-44 bg-transparent text-xs text-white placeholder:text-white/30 focus:outline-none" />
            {query && <button type="button" onClick={() => { setSearch(""); setQuery(""); }} className="text-white/50 hover:text-white">✕</button>}
          </form>
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div> : (
          <div className="overflow-hidden rounded-xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50">
                <tr><th className="px-4 py-3">Garage</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Verified</th><th className="px-4 py-3">Last login</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">MRR</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {garages.map((g) => (
                  <tr key={g.id} onClick={() => navigate(`/owner/garage/${g.id}`)} data-testid={`admin-garage-${g.id}`} className="cursor-pointer text-white/90 hover:bg-white/5">
                    <td className="px-4 py-3 font-bold">{g.garage_name}{g.suspended && <span className="ml-2 rounded bg-danger/30 px-1.5 text-[10px] font-bold text-red-300">SUSPENDED</span>}</td>
                    <td className="px-4 py-3 text-white/60">{g.owner_email}</td>
                    <td className="px-4 py-3">{g.owner_email_verified ? <span className="rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-bold text-success">VERIFIED</span> : <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-300"><MailWarning className="h-3 w-3" />UNVERIFIED</span>}</td>
                    <td className="px-4 py-3 text-white/50" title={g.owner_last_login ? fmtDateTime(g.owner_last_login) : ""}>{g.owner_last_login ? fmtDateTime(g.owner_last_login) : "Never"}</td>
                    <td className="px-4 py-3 uppercase">{g.plan || "—"} {g.demo_mode && <span className="text-[10px] text-white/40">(demo)</span>}</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold uppercase">{g.status}</span></td>
                    <td className="px-4 py-3 text-right font-display font-bold">£{(g.discounted_monthly_gbp ?? g.monthly_amount_gbp ?? 0).toLocaleString()}{g.discount && <span className="ml-1 text-[10px] text-brand">▼</span>}</td>
                  </tr>
                ))}
                {garages.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40">No garages in this segment.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSec && <SecurityPanel admin={admin} onClose={() => setShowSec(false)} />}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, testid, onClick, sub }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`rounded-xl border p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-brand ${accent ? "border-brand/40 bg-brand/10" : "border-white/10 bg-white/5"} ${onClick ? "" : "cursor-default"}`}>
      <Icon className={`mb-2 h-5 w-5 ${accent ? "text-brand" : "text-white/50"}`} />
      <p className="font-display text-3xl font-bold text-white">{value}</p>
      <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/50">{label}{sub && <span className="ml-1 normal-case text-white/30">({sub})</span>}</p>
    </button>
  );
}

function Panel({ title, children, empty }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-white/50">{title}</h3>
      {empty ? <p className="py-2 text-sm text-white/30">None</p> : <div className="divide-y divide-white/5">{children}</div>}
    </div>
  );
}

function SecurityPanel({ admin, onClose }) {
  const [enabled, setEnabled] = useState(!!admin?.totp_enabled);
  const [enroll, setEnroll] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const begin = async () => {
    setBusy(true); setMsg(null);
    try { setEnroll(await adminApi("/admin/auth/2fa/begin", { method: "POST" })); }
    catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setMsg(null);
    try { await adminApi("/admin/auth/2fa/enable", { method: "POST", body: JSON.stringify({ code: code.trim() }) }); setEnabled(true); setEnroll(null); setMsg("2FA enabled. It will be required at next sign-in."); }
    catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setMsg(null);
    try { await adminApi("/admin/auth/2fa/disable", { method: "POST", body: JSON.stringify({ code: code.trim() }) }); setEnabled(false); setCode(""); setMsg("2FA disabled."); }
    catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="security-panel">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-midnight-2 p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 font-display text-xl font-bold uppercase text-white">Two-Factor Auth</h3>
        <p className="mb-4 text-sm text-white/50">Protect the owner portal with an authenticator app.</p>

        {enabled ? (
          <div className="space-y-3">
            <p className="rounded-lg bg-success/10 px-3 py-2 text-sm text-success">2FA is currently ENABLED.</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Current code to disable" data-testid="twofa-disable-code" className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-center tracking-widest text-white placeholder:text-white/30" />
            <button onClick={disable} disabled={busy} data-testid="twofa-disable-btn" className="w-full rounded-lg border border-danger/40 bg-danger/10 py-2.5 text-sm font-bold text-red-300">Disable 2FA</button>
          </div>
        ) : enroll ? (
          <div className="space-y-3">
            <p className="text-sm text-white/70">Add this secret to your authenticator app (Google Authenticator, Authy, 1Password):</p>
            <code data-testid="twofa-secret" className="block break-all rounded-lg bg-white/5 px-3 py-2 text-center text-sm font-bold tracking-wider text-brand">{enroll.secret}</code>
            <p className="text-[11px] text-white/40 break-all">{enroll.otpauth_uri}</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter 6-digit code" data-testid="twofa-confirm-code" className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-center tracking-widest text-white placeholder:text-white/30" />
            <button onClick={confirm} disabled={busy} data-testid="twofa-confirm-btn" className="w-full rounded-lg bg-brand py-2.5 text-sm font-extrabold tracking-widest text-white">VERIFY & ENABLE</button>
          </div>
        ) : (
          <button onClick={begin} disabled={busy} data-testid="twofa-begin-btn" className="w-full rounded-lg bg-brand py-2.5 text-sm font-extrabold tracking-widest text-white">{busy ? "…" : "ENABLE 2FA"}</button>
        )}
        {msg && <p data-testid="twofa-msg" className="mt-3 text-sm text-white/70">{msg}</p>}
        <button onClick={onClose} className="mt-4 w-full rounded-lg border border-white/15 py-2.5 text-sm font-bold text-white/70">Close</button>
      </div>
    </div>
  );
}
